import React from 'react';
import { Row, Col, Typography, Space } from 'antd';

import { SectionWrapper } from 'common/components';

import { OverlayValueItem } from 'common/components/overlay';
import { findDimensionEnumDisplay } from '../../mappers/mapToEnums';
import { useGetProductMeasures } from 'pages/product-full-view/hooks';

import { isEqual } from 'lodash';

const { Text } = Typography;

const ProductOverviewDimension = ({
  showPackageDrawer,
  originProduct,
  product,
  showOverlay,
  productEnums,
}) => {
  const productMeasures = useGetProductMeasures({ product });
  const isShowSupplied =
    isNaN(parseFloat(productMeasures?.metricHeight)) &&
    isNaN(parseFloat(productMeasures?.metricWidth)) &&
    isNaN(parseFloat(productMeasures?.metricDepth)) &&
    isNaN(parseFloat(productMeasures?.metricWeight));
  const isShowValidated =
    isNaN(parseFloat(productMeasures?.imperialHeight)) &&
    isNaN(parseFloat(productMeasures?.imperialWidth)) &&
    isNaN(parseFloat(productMeasures?.imperialDepth)) &&
    isNaN(parseFloat(productMeasures?.imperialWeight));
  const originProductMeasures = useGetProductMeasures({
    product: originProduct,
  });

  const formatValue = ({ fieldName, type, isOrigin }) => {
    const measurementData = isOrigin ? originProductMeasures : productMeasures;

    if (['imperialDimensionUOM'].includes(type)) {
      return formatImperialDimension(
        measurementData?.[fieldName],
        measurementData?.imperialDimensionUOM
      );
    }

    if (['imperialWeightUOM', 'metricWeightUOM'].includes(type)) {
      return calulateSecondDimension(
        measurementData?.[fieldName],
        measurementData?.[type],
        productEnums
      );
    }

    if (['metricDimensionUOM'].includes(type)) {
      return formatMetricDimension(
        measurementData?.[fieldName],
        measurementData?.metricDimensionUOM
      );
    }
  };

  const defaultOverlayValueItemProps = (fieldName, type) => {
    const originValue = formatValue({ fieldName, type, isOrigin: true });
    const overlayValue = formatValue({ fieldName, type, isOrigin: false });

    return {
      originValue: originValue || 'N/A',
      showOverlay: showOverlay && !isEqual(originValue, overlayValue),
    };
  };

  const onArrangeDimensionInfo = () => {
    const imperialDimension = [
      {
        label: 'H',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'imperialHeight',
              'imperialDimensionUOM'
            )}
          >
            {formatImperialDimension(
              productMeasures?.imperialHeight,
              productMeasures?.imperialDimensionUOM
            )}
          </OverlayValueItem>
        ),
      },
      {
        label: 'W',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'imperialWidth',
              'imperialDimensionUOM'
            )}
          >
            {formatImperialDimension(
              productMeasures?.imperialWidth,
              productMeasures?.imperialDimensionUOM
            )}
          </OverlayValueItem>
        ),
      },
      {
        label: 'D',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'imperialDepth',
              'imperialDimensionUOM'
            )}
          >
            {formatImperialDimension(
              productMeasures?.imperialDepth,
              productMeasures?.imperialDimensionUOM
            )}
          </OverlayValueItem>
        ),
      },
      {
        label: 'Weight',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'imperialWeight',
              'imperialWeightUOM'
            )}
          >
            {calulateSecondDimension(
              productMeasures?.imperialWeight,
              productMeasures?.imperialWeightUOM,
              productEnums
            )}
          </OverlayValueItem>
        ),
      },
    ];

    const metricDimension = [
      {
        label: 'H',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'metricHeight',
              'metricDimensionUOM'
            )}
          >
            {formatMetricDimension(
              productMeasures?.metricHeight,
              productMeasures?.metricDimensionUOM
            )}
          </OverlayValueItem>
        ),
      },
      {
        label: 'W',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'metricWidth',
              'metricDimensionUOM'
            )}
          >
            {formatMetricDimension(
              productMeasures?.metricWidth,
              productMeasures?.metricDimensionUOM
            )}
          </OverlayValueItem>
        ),
      },
      {
        label: 'D',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps(
              'metricDepth',
              'metricDimensionUOM'
            )}
          >
            {formatMetricDimension(
              productMeasures?.metricDepth,
              productMeasures?.metricDimensionUOM
            )}
          </OverlayValueItem>
        ),
      },
      {
        label: 'Weight',
        info: (
          <OverlayValueItem
            {...defaultOverlayValueItemProps('metricWeight', 'metricWeightUOM')}
          >
            {calulateSecondDimension(
              productMeasures?.metricWeight,
              productMeasures?.metricWeightUOM,
              productEnums
            )}
          </OverlayValueItem>
        ),
      },
    ];
    const dimensions = {
      imperial: imperialDimension,
      metric: metricDimension,
    };
    // const dimensions = interleave(imperialDimension, metricDimension);
    return dimensions;
  };

  const formatImperialDimension = (size, imperialDimensionUOM) =>
    `${
      isNaN(parseFloat(size))
        ? 'N/A'
        : Math.round(parseFloat(size) * 100) / 100 +
          ' ' +
          (imperialDimensionUOM || '')
    } `;

  const formatMetricDimension = (size, metricDimensionUOM) =>
    `${
      isNaN(parseFloat(size))
        ? 'N/A'
        : Math.round(parseFloat(size) * 100) / 100 +
          ' ' +
          (metricDimensionUOM || '')
    } `;

  const interleave = (arr1, arr2) => {
    let newArr = [];
    arr1.forEach((ele, index) => {
      newArr.push(ele, arr2[index]);
    });
    return newArr;
  };

  const renderDimensionItem = (item, index) => {
    const { infoAppend, info, label } = item;
    return (
      <Row key={index} style={{ marginBottom: 2 }}>
        <Space align='baseline'>
          <Text style={{ fontWeight: 700 }}>{label}:</Text>
          <Text className='product-overview__metadata-dimensions-info-text'>
            {info}
          </Text>
          {infoAppend ? infoAppend : null}
        </Space>
      </Row>
    );
  };

  const productDimensionInfo = onArrangeDimensionInfo();

  return (
    <Row className='product-overview__metadata-dimensions' gutter={[8, 8]}>
      {!isShowSupplied && (
        <Col flex={1}>
          <SectionWrapper title={'Supplied'}>
            {productDimensionInfo?.metric?.map(renderDimensionItem)}
          </SectionWrapper>
        </Col>
      )}
      {!isShowValidated && (
        <Col flex={1}>
          <SectionWrapper title={'Validated'}>
            {productDimensionInfo?.imperial?.map(renderDimensionItem)}
          </SectionWrapper>
        </Col>
      )}
    </Row>
  );
};

const calulateSecondDimension = (value, uomValue, productEnums) => {
  const finalUomValue =
    findDimensionEnumDisplay(uomValue, productEnums) || uomValue;

  return isNaN(parseFloat(value))
    ? 'N/A'
    : Math.round(parseFloat(value) * 100) / 100 + ' ' + (finalUomValue || '');
};

export default ProductOverviewDimension;
